import { Component } from '@theme/component';

/**
 * @typedef {Object} FinderRefs
 * @property {HTMLAnchorElement} ctaButton
 */

/** @extends {Component<FinderRefs>} */
class CollectionFinder extends Component {
    /** @type {(ev: MouseEvent) => void} */
    #docHandler;

    connectedCallback() {
        super.connectedCallback();
        /** @type {NodeListOf<HTMLDetailsElement>} */
        this.controls = this.querySelectorAll('details.finder-dropdown');

        // Default: select first value in each dropdown
        this.#initializeDefaults();
        this.#updateButton();

        this.addEventListener('click', (e) => {
            /** @type {HTMLElement|null} */
            const target = /** @type {any} */ (e.target);
            /** @type {HTMLElement|null} */
            const option = target && target.closest ? target.closest('.finder-option') : null;
            if (!option) return;
            /** @type {HTMLElement|null} */
            const details = option.closest('details.finder-dropdown');
            if (!details) return;

            // update selection UI
            details
                .querySelectorAll('.finder-option[aria-selected]')
                .forEach((el) => (/** @type {HTMLElement} */ (el)).setAttribute('aria-selected', 'false'));
            option.setAttribute('aria-selected', 'true');

            /** @type {HTMLElement|null} */
            const label = details.querySelector('[data-current]');
            if (label) label.textContent = option.textContent.trim();

            // store chosen value on the control
            details.dataset.value = option.dataset.value;

      // close panel
      /** @type {any} */ (details).open = false;
            this.#closeOthers(/** @type {HTMLDetailsElement} */(details));

            this.#updateButton();
        });

        // Close other dropdowns when one opens and open on summary click
        this.controls.forEach((d) => {
            const summary = d.querySelector('summary');
            if (summary) {
                summary.addEventListener('click', (ev) => {
                    ev.preventDefault();
                    const willOpen = !(/** @type {any} */ (d)).open;
                    this.#closeOthers(null);
                    (/** @type {any} */ (d)).open = willOpen;
                });
            }
        });

        // Click outside to close all
        this.#docHandler = (evt) => {
            const t = /** @type {any} */ (evt && evt.target);
            if (!this.contains(t)) this.#closeAll();
        };
        document.addEventListener('click', this.#docHandler);
    }

    #updateButton() {
        /** @type {NodeListOf<HTMLElement>} */
        const controls = this.querySelectorAll('details.finder-dropdown');
        const params = [];
        for (const control of controls) {
            const value = (control.dataset.value || '').trim();
            if (value) params.push(value);
        }

        const host = window.location.origin;
        const base = this.dataset.baseUrl || '/collections';
        const baseUrl = new URL(base, host);
        const href = params.length ? `${baseUrl.pathname}?${params.join('&')}` : baseUrl.pathname;

        if (this.refs.ctaButton) {
            this.refs.ctaButton.href = href;
            this.refs.ctaButton.setAttribute('aria-disabled', params.length ? 'false' : 'true');
        }
    }

    #initializeDefaults() {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);

        this.controls && this.controls.forEach((details) => {
            const filterKey = details.dataset.filterKey;
            let selectedOption = null;

            // Try to find matching option from URL parameters
            if (filterKey) {
                const urlParamValue = urlParams.get(`filter.p.m.custom.${filterKey}`);
                if (urlParamValue) {
                    // Decode the URL parameter value
                    const decodedParamValue = decodeURIComponent(urlParamValue);

                    // Find the option that matches the URL parameter
                    const options = details.querySelectorAll('.finder-option');
                    for (const option of options) {
                        const optionValue = option.dataset.value;
                        if (optionValue) {
                            // Decode the option value for comparison
                            const decodedOptionValue = decodeURIComponent(optionValue);
                            // Check if the decoded values match
                            if (decodedOptionValue.includes(decodedParamValue) || decodedParamValue.includes(decodedOptionValue.split('=')[1])) {
                                selectedOption = option;
                                break;
                            }
                        }
                    }
                }
            }

            // If no URL match found, use first option as default
            if (!selectedOption) {
                selectedOption = details.querySelector('.finder-option');
            }

            if (!selectedOption) return;

            // mark selected
            selectedOption.setAttribute('aria-selected', 'true');

            // set label
            const label = details.querySelector('[data-current]');
            if (label) label.textContent = selectedOption.textContent.trim();

            // set dataset value
            details.dataset.value = selectedOption.dataset.value || '';

            // ensure closed by default
            (/** @type {any} */ (details)).open = false;
        });
    }

    /**
     * @param {HTMLDetailsElement|null} except
     */
    #closeOthers(except) {
        this.controls && this.controls.forEach((d) => {
            if (d !== except) (/** @type {any} */ (d)).open = false;
        });
    }

    #closeAll() {
        this.#closeOthers(/** @type {any} */(null));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.#docHandler);
    }
}

customElements.define('collection-finder', CollectionFinder);



